'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var count = 0;
var DEFAULT_CALLBACK_DELETE_DELAY_MS = 5000; // 기본 콜백 삭제 지연 시간 (5초)
/**
 * NOL Bridge에서 사용할 수 있는 콜백 함수를 반환하는 함수. 함수명과 함수 결과 콜백 Promise를 반환
 * window._nolBridgeCallback에 콜백 함수를 등록하여, 앱 입장에서 해당 메서드를 실행한 후 응답이 있을 때
 * 해당 공간에 등록된 콜백 함수를 호출할 수 있도록 합니다.
 *
 * 메모리 누수를 방지하기 위해 콜백 함수는 일정 시간 후에 자동으로 삭제됩니다.
 * @param fn - 콜백 함수
 * @param fnName - 콜백 함수의 이름
 * @param deleteDelayMs - 콜백 함수 삭제 지연 시간 (기본값: 5000ms)
 * @returns 콜백 함수의 이름과 Promise 객체
 */
function getNolBridgeCallbackFunction(fn, fnName, // 함수명
deleteDelayMs // 콜백 함수 삭제 지연 시간 (기본값: 5000ms)
) {
    var cbName = fnName + "_" + count++ || "cb_" + count++;
    var _deleteDelayMs = deleteDelayMs !== null && deleteDelayMs !== void 0 ? deleteDelayMs : DEFAULT_CALLBACK_DELETE_DELAY_MS;
    var functionFullName = cbName.includes('window') ? cbName : "window._nolBridgeCallback." + cbName;
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- reject funct
    if (typeof window === "undefined")
        return [functionFullName, Promise.reject()];
    if (!window._nolBridgeCallback) {
        window._nolBridgeCallback = {};
    }
    // 결과를 Promise 객체로 받을 수 있도록 함
    var promise = new Promise(function (resolve) {
        var timeoutIdx = setTimeout(function () {
            fn === null || fn === void 0 ? void 0 : fn({ code: false, value: {} });
            resolve({ code: false, value: {} });
            if (typeof window !== "undefined") {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- 사용한 콜백 함수 제거
                delete window._nolBridgeCallback[cbName];
            }
        }, _deleteDelayMs);
        // 함수 등록
        window._nolBridgeCallback[cbName] = function (code, _value) {
            if (_value === void 0) { _value = "{}"; }
            clearTimeout(timeoutIdx);
            var value = JSON.parse(_value);
            fn === null || fn === void 0 ? void 0 : fn({ code: code, value: value });
            resolve({ code: code, value: value });
            if (typeof window !== "undefined") {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- 사용한 콜백 함수 제거
                delete window._nolBridgeCallback[cbName];
            }
        };
    });
    return [functionFullName, promise];
}

/**
 * NOL App webview detection expression — *string form*.
 *
 * Inline script / build-time codegen 등 *함수 호출 불가능한 컨텍스트* 에서
 * 동일한 detect mechanism 을 적용하기 위한 single source.
 *
 * `isIOS() || isAndroid()` 와 동일 결과.
 */
var DETECT_EXPRESSION = 'typeof window!=="undefined"&&(!!(window.webkit&&window.webkit.messageHandlers&&window.webkit.messageHandlers.nolApp)||!!window.nolApp)';
var isIOS = function () { var _a, _b; return typeof window !== 'undefined' && !!((_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.nolApp); };
var isAndroid = function () { return typeof window !== 'undefined' && !!window.nolApp; };
/**
 * 각 앱별 동작을 위한 NolBridge
 */
var instance = {
    /**
     * Nol-Bridge가 동작 가능한 환경인지 반환
     */
    get isEnabled() {
        return true;
    },
    brazeLogPurchase: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'brazeLogPurchase'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'tracking',
            command: 'brazeLogPurchase',
            callback: name,
        });
        return promise;
    },
    shareToNative: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'shareToNative'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'share',
            command: 'shareToNative',
            callback: name,
        });
        return promise;
    },
    brazeEvent: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'brazeEvent'), name = _a[0], promise = _a[1];
        // 전달받은 braze parameter의 값 치환
        var replacedEventParam = Object.assign({}, param.eventParam);
        for (var _i = 0, _b = Object.keys(replacedEventParam); _i < _b.length; _i++) {
            var key = _b[_i];
            var value = replacedEventParam[key];
            // Date 객체는 ISO-8601을 따르는 문자열로 변환
            if (value instanceof Date) {
                replacedEventParam[key] = value.toISOString();
            }
        }
        _dispatch({
            param: JSON.stringify({
                eventName: param.eventName,
                eventParam: replacedEventParam,
            }),
            domain: 'tracking',
            command: 'brazeEvent',
            callback: name,
        });
        return promise;
    },
    brazeAttribute: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'brazeAttribute'), name = _a[0], promise = _a[1];
        // 전달받은 braze parameter의 값 치환
        var replacedAttributeValue = param.attributeValue;
        if (Array.isArray(replacedAttributeValue)) {
            // 배열인 경우 배열 내의 Date 치환
            for (var i = 0; i < replacedAttributeValue.length; i++) {
                var value = replacedAttributeValue[i];
                // Date 객체는 ISO-8601을 따르는 문자열로 변환
                if (value instanceof Date) {
                    replacedAttributeValue[i] = value.toISOString();
                }
            }
        }
        else if (replacedAttributeValue instanceof Date) {
            // Date 객체인 경우 치환
            replacedAttributeValue = replacedAttributeValue.toISOString();
        }
        _dispatch({
            param: JSON.stringify({
                attributeName: param.attributeName,
                attributeMethod: param.attributeMethod,
                attributeValue: replacedAttributeValue,
            }),
            domain: 'tracking',
            command: 'brazeAttribute',
            callback: name,
        });
        return promise;
    },
    saveOfflineTicket: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'saveOfflineTicket'), name = _a[0], promise = _a[1];
        // param은 JSON.stringify로 변환하여 전달
        _dispatch({
            param: JSON.stringify(param),
            domain: 'ticket',
            command: 'saveOfflineTicket',
            callback: name,
        });
        return promise;
    },
    deleteOfflineTicket: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'deleteOfflineTicket'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'ticket',
            command: 'deleteOfflineTicket',
            callback: name,
        });
        return promise;
    },
    setScreenshotPrevention: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'setScreenshotPrevention'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'ticket',
            command: 'setScreenshotPrevention',
            callback: name,
        });
        return promise;
    },
    setMaxBrightness: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'setMaxBrightness'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'common',
            command: 'setMaxBrightness',
            callback: name,
        });
        return promise;
    },
    shareToKakao: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'shareToKakao'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'share',
            command: 'shareToKakao',
            callback: name,
        });
        return promise;
    },
    shareViaSMS: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'shareViaSMS'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'share',
            command: 'shareViaSMS',
            callback: name,
        });
        return promise;
    },
    shareToClipboard: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'shareToClipboard'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'share',
            command: 'shareToClipboard',
            callback: name,
        });
        return promise;
    },
    nolTrackEvent: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'nolTrackEvent'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'nol',
            command: 'nolTrackEvent',
            callback: name,
        });
        return promise;
    },
    home: function (cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'home'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify({}),
            domain: 'common',
            command: 'home',
            callback: name,
        });
        return promise;
    },
    openFacePassSDK: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'openFacePassSDK'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'auth',
            command: 'openFacePassSDK',
            callback: name,
        });
        return promise;
    },
    appsflyer: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'appsflyer'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'tracking',
            command: 'appsflyer',
            callback: name,
        });
        return promise;
    },
    kakaoAd: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'kakaoAd'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'tracking',
            command: 'kakaoAD',
            callback: name,
        });
        return promise;
    },
    facebook: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'facebook'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'tracking',
            command: 'facebook',
            callback: name,
        });
        return promise;
    },
    setSwipeBack: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'setSwipeBack'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'common',
            command: 'setSwipeBack',
            callback: name,
        });
        return promise;
    },
    backKeyFunc: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'backKeyFunc'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify({ func: param.fnName }),
            domain: 'common',
            command: 'backKeyFunc',
            callback: name,
        });
        return promise;
    },
    nolAppInfo: function (cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'nolAppInfo'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify({}),
            domain: 'auth',
            command: 'nolAppInfo',
            callback: name,
        });
        return promise;
    },
    refreshNolUserInfo: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'refreshNolUserInfo'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'auth',
            command: 'refreshNolUserInfo',
            callback: name,
        });
        return promise;
    },
    openSignIn: function (cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'openSignIn'), name = _a[0], promise = _a[1];
        _dispatch({ param: JSON.stringify({}), domain: 'auth', command: 'openSignIn', callback: name });
        return promise;
    },
    openInAppBrowser: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'openInAppBrowser'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'common',
            command: 'openInAppBrowser',
            callback: name,
        });
        return promise;
    },
    inpkLoadUrlAfterClose: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'inpkLoadUrlAfterClose'), name = _a[0], promise = _a[1];
        _dispatch({ param: JSON.stringify(param || {}), domain: 'inpk', command: 'inpkLoadUrlAfterClose', callback: name });
        return promise;
    },
    openSocialAuthSDK: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        /**
         * openSocialAuthSDK 에서만 deleteDelayMs 를 따로 받는 이유
         *
         * 다른 메서드의 경우, callback 함수를 호출하는 타이밍이 대략적으로 5초 이내이다.
         * 다만 ios의 openSocialAuthSDK 의 경우, 소셜로그인의 로직을 다 block 한 후 _nolBridgeCallback의 함수를 호출하므로,
         * 디폴트값인 5초 이후의 콜백을 실행할 수 있는 구조이다.
         *
         * 사용하는 곳에서 response를 직접 참조하는 부분이 없으므로 앱 사용에 문제는 없지만
         * 필요없는 에러로그가 쌓이므로 삭제 타이밍을 늦춰 제어한다.
         * */
        var SOCIAL_AUTH_SDK_TIMEOUT_MS = 5 * 60 * 1000; // 5분
        var _a = getNolBridgeCallbackFunction(cb, 'openSocialAuthSDK', SOCIAL_AUTH_SDK_TIMEOUT_MS), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param || {}),
            domain: 'auth',
            command: 'openSocialAuthSDK',
            callback: name,
        });
        return promise;
    },
    signIn: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'signIn'), name = _a[0], promise = _a[1];
        _dispatch({
            param: JSON.stringify(param),
            domain: 'auth',
            command: 'signIn',
            callback: name,
        });
        return promise;
    },
    openWebView: function (param, cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'openWebView'), name = _a[0], promise = _a[1];
        _dispatch({ param: JSON.stringify(param || {}), domain: 'common', command: 'openWebView', callback: name });
        return promise;
    },
    closeWebView: function (cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'closeWebView'), name = _a[0], promise = _a[1];
        _dispatch({ param: JSON.stringify({}), domain: 'common', command: 'closeWebView', callback: name });
        return promise;
    },
    signOut: function (cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'signOut'), name = _a[0], promise = _a[1];
        _dispatch({ param: JSON.stringify({}), domain: 'auth', command: 'signOut', callback: name });
        return promise;
    },
    withdraw: function (cb) {
        if (cb === void 0) { cb = function () { return void 0; }; }
        var _a = getNolBridgeCallbackFunction(cb, 'withdraw'), name = _a[0], promise = _a[1];
        _dispatch({ param: JSON.stringify({}), domain: 'member', command: 'withdraw', callback: name });
        return promise;
    },
};
/**
 * 직접 실행 금지 - 각 환경에 맞는 Javascript Interface 함수를 실행하는 내부 함수
 */
function _dispatch(params) {
    var _a, _b, _c, _d, _e, _f;
    if (isAndroid()) {
        (_b = (_a = window === null || window === void 0 ? void 0 : window.nolApp) === null || _a === void 0 ? void 0 : _a.action) === null || _b === void 0 ? void 0 : _b.call(_a, JSON.stringify(params));
    }
    else if (isIOS()) {
        (_f = (_e = (_d = (_c = window === null || window === void 0 ? void 0 : window.webkit) === null || _c === void 0 ? void 0 : _c.messageHandlers) === null || _d === void 0 ? void 0 : _d.nolApp) === null || _e === void 0 ? void 0 : _e.postMessage) === null || _f === void 0 ? void 0 : _f.call(_e, params);
    }
}
/**
 * 웹뷰 환경인지 여부에 따라 nol bridge instance를 설정
 */
var NolBridge = isIOS() || isAndroid() ? instance : { isEnabled: false };

exports.DETECT_EXPRESSION = DETECT_EXPRESSION;
exports.NolBridge = NolBridge;
